import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, coerceBooleanFromString, projectIdParamsSchema } from '../schemas';
import { professionSchema } from '../../interfaces/IProfession';
import { baseProfessionSchema } from '../../interfaces/IBaseProfession';
import { tradeSchema } from '../../interfaces/ITrade';
import { professionDisplaySettingsSchema } from '../../interfaces/IProfessionDisplaySettings';

// ============================================================
// Param Schemas
// ============================================================

const projectProfessionParamsSchema = z.object({
	projectId: requiredStringSchema,
	professionId: requiredStringSchema.describe('Profession ID'),
});

// ============================================================
// Query Schemas
// ============================================================

const getProfessionsQuerySchema = z.object({
	includeDeleted: coerceBooleanFromString.optional(),
	includeInactive: coerceBooleanFromString.optional(),
	startDate: z.string().optional(),
	endDate: z.string().optional(),
});

// ============================================================
// Body Schemas
// ============================================================

/** `projectId` is addressed by the URI, so it must not also travel in the body. */
const createProfessionBodySchema = baseProfessionSchema.omit({ projectId: true });

/**
 * `projectId` is absent by construction. The service used to `delete` it from a
 * clone at runtime, which is a fix that only holds as long as nobody removes the
 * line; omitting it from the schema is the fix that cannot be undone by accident.
 */
const updateProfessionBodySchema = professionSchema.omit({ _id: true, projectId: true }).partial();

const updateDisplaySettingsBodySchema = professionDisplaySettingsSchema;

/**
 * Was a BARE ARRAY of ids. An array body exposes no top-level key to the
 * membership guard, which is half of why the bulk soft-delete underneath it ran
 * with no project clause at all.
 */
const deleteProfessionsByListBodySchema = z.object({
	ids: z.array(z.string()).min(1),
});

const updateProfessionsTradeBodySchema = tradeSchema;

// ============================================================
// Response Schemas
// ============================================================

const deletedCountResponseSchema = z.object({
	deleted: z.number(),
});

// ============================================================
// Contract
// ============================================================

/**
 * Professions are project data: `/projects/:projectId/professions`.
 *
 * A profession is an ORG-LEVEL document with two project relations, and the two
 * are used for different things:
 *
 *  - `projectId` — the project that CREATED it. Immutable, and the field every
 *    id-addressed read and write scopes on: a write should require ownership,
 *    not mere reachability.
 *  - `activeProjects.<projectId>` (`$exists`) — REACHABLE in this project. The
 *    field the list reads scope on, so a profession created in project A and
 *    activated in project B shows up in B's roster.
 *
 * A profession created in A and activated in B is therefore listed for B but
 * 404s a scoped read-by-id for a B-only member. That is deliberate.
 *
 * `{ root: true }` — `updateProfessionsTrade` is NOT project data (global admin)
 * and spells its old `/professions/updateTrade` path literally. `getProfessions`
 * had to move regardless: its path was a bare `/:projectId`, which under `root`
 * becomes `/api/projectConfig/:projectId`, a catch-all shadowing every sibling.
 *
 * IMPORTANT: literal sub-paths (`/working`, `/unassigned`, `/displaySettings`)
 * must be declared BEFORE the parameterized `/:professionId`.
 */
export const professionContract = defineContract(
	'professions',
	{
		/**
		 * Global admin. Entities calls this on a trade rename and every profession
		 * carrying that trade must follow, across every organization and project.
		 */
		updateProfessionsTrade: route({
			method: 'PUT',
			path: '/professions/updateTrade',
			body: updateProfessionsTradeBodySchema,
			response: z.string(),
			operationId: 'updateProfessionsTrade',
			tags: ['Profession'],
			summary: 'Propagate a renamed trade onto every profession carrying it',
		}),
		getProfessions: route({
			method: 'GET',
			path: '/projects/:projectId/professions',
			params: projectIdParamsSchema,
			query: getProfessionsQuerySchema,
			response: z.array(professionSchema),
			operationId: 'getProfessions',
			tags: ['Profession'],
			summary: 'List the professions reachable in a project',
		}),
		createProfession: route({
			method: 'POST',
			path: '/projects/:projectId/professions',
			params: projectIdParamsSchema,
			body: createProfessionBodySchema,
			response: professionSchema,
			operationId: 'createProfession',
			tags: ['Profession'],
			summary: 'Create a profession owned by a project',
		}),
		/**
		 * Bulk soft-delete. Foreign ids are a no-op reported as `200 {deleted: 0}`
		 * — Issues' precedent — rather than the 400 this used to throw, which
		 * leaked "none of these ids exist in your project".
		 */
		deleteProfessionsByProfessionList: route({
			method: 'DELETE',
			path: '/projects/:projectId/professions',
			params: projectIdParamsSchema,
			body: deleteProfessionsByListBodySchema,
			response: deletedCountResponseSchema,
			operationId: 'deleteProfessionsByProfessionList',
			tags: ['Profession'],
			summary: 'Soft-delete several professions of a project by id',
		}),
		getWorkingProfessions: route({
			method: 'GET',
			path: '/projects/:projectId/professions/working',
			params: projectIdParamsSchema,
			query: getProfessionsQuerySchema,
			response: z.array(professionSchema),
			operationId: 'getWorkingProfessions',
			tags: ['Profession'],
			summary: 'List the working professions of a project',
		}),
		/**
		 * The underlying query is by `organizationId` — the handler resolves
		 * project → organization first. The project stays in the URI because it is
		 * what the caller holds and what the guard validates; the org lookup is a
		 * server-side derivation.
		 */
		getUnassignedProfession: route({
			method: 'GET',
			path: '/projects/:projectId/professions/unassigned',
			params: projectIdParamsSchema,
			response: professionSchema,
			operationId: 'getUnassignedProfession',
			tags: ['Profession'],
			summary: "Get the unassigned profession of a project's organization",
		}),
		updateProjectProfessionDisplaySettings: route({
			method: 'PUT',
			path: '/projects/:projectId/professions/displaySettings',
			params: projectIdParamsSchema,
			body: updateDisplaySettingsBodySchema,
			response: z.string(),
			operationId: 'updateProjectProfessionDisplaySettings',
			tags: ['Profession'],
			summary: 'Update the profession display settings of a project',
		}),
		getProfession: route({
			method: 'GET',
			path: '/projects/:projectId/professions/:professionId',
			params: projectProfessionParamsSchema,
			response: professionSchema,
			operationId: 'getProfession',
			tags: ['Profession'],
			summary: 'Get a profession owned by a project',
		}),
		updateProfession: route({
			method: 'PUT',
			path: '/projects/:projectId/professions/:professionId',
			params: projectProfessionParamsSchema,
			body: updateProfessionBodySchema,
			response: z.string(),
			operationId: 'updateProfession',
			tags: ['Profession'],
			summary: 'Update a profession owned by a project',
		}),
		deleteProfession: route({
			method: 'DELETE',
			path: '/projects/:projectId/professions/:professionId',
			params: projectProfessionParamsSchema,
			response: z.string(),
			operationId: 'deleteProfession',
			tags: ['Profession'],
			summary: 'Soft-delete a profession owned by a project',
		}),
	},
	{ root: true }
);
